import { Container, Spacer, SpacerVariant, spacerOptions } from "../components/layout"
import React, { useEffect, useState } from "react"
import { Repeater, types, useAdminContext } from "react-bricks"

import Loading from "../../shared/components/miscellaneous/loading"
import { useQuery } from "@tanstack/react-query"
import { useSDK } from "../../shared/context/client"
import { useUrlSearchParams } from "../../shared/hooks/use-url-search-params"

interface NpsProps {
    spacerTop: boolean
    spacerBottom: boolean
    variantTop?: SpacerVariant
    variantBottom?: SpacerVariant
}

const Nps: types.Brick<NpsProps> = ({
    spacerTop,
    spacerBottom,
    variantTop = SpacerVariant.Small,
    variantBottom = SpacerVariant.Small,
}) => {
    const { isAdmin } = useAdminContext()

    const [params, setParams] = useUrlSearchParams()
    const client = useSDK()
    const [match, setMatch] = useState(true)

    const { data, isPending, isError } = useQuery({
        queryKey: ["getCustomerAttributes", { customer_id: params.i }],
        queryFn: () =>
            client.getCustomerAttributes({
                customer_id: params.i + "",
                attributes: [
                    { type: "property", property: "shortlink_slug" },
                    { type: "segmentation", id: process.env.EXPONEA_NPS_SEGMENTATION },
                ],
            }),
        enabled: !!params.i && !!process.env.EXPONEA_NPS_SEGMENTATION,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        meta: { background: true },
    })

    useEffect(() => {
        if (!data?.data) return

        const segment =
            data.data.find(
                attr =>
                    attr.type === "segmentation" &&
                    attr.id === process.env.EXPONEA_NPS_SEGMENTATION &&
                    attr.value !== null
            ) || false
        const match =
            data.data.find(
                attr => attr.type === "property" && attr.property === "shortlink_slug" && attr.value === params.s
            ) || false

        if (!match || !params.s) {
            setParams({ r: undefined })
            return setMatch(false)
        }

        if (segment) return setParams({ r: undefined })

        if (params.r) return (window.location.href = params.r)
    }, [data, params.s, params.r])

    useEffect(() => {
        if (!isError) return

        setParams({ r: undefined })
    }, [isError])

    if (!isAdmin && isPending) {
        return <Loading loading />
    }

    return (
        <>
            <Spacer variant={!spacerTop ? SpacerVariant.None : variantTop} />
            <Container>
                {isAdmin ? (
                    <>
                        <Repeater propName="errorText" itemProps={{ spacerTop: false, spacerBottom: false }} />
                        <Spacer variant={SpacerVariant.Medium} />
                        <Repeater propName="participatedText" itemProps={{ spacerTop: false, spacerBottom: false }} />
                    </>
                ) : isError || !match ? (
                    <Repeater propName="errorText" itemProps={{ spacerTop: false, spacerBottom: false }} />
                ) : (
                    <Repeater propName="participatedText" itemProps={{ spacerTop: false, spacerBottom: false }} />
                )}
            </Container>
            <Spacer variant={!spacerBottom ? SpacerVariant.None : variantBottom} />
        </>
    )
}

Nps.schema = {
    name: "NPS",
    label: "NPS",
    repeaterItems: [
        {
            name: "errorText",
            items: [
                {
                    type: "BasicText",
                    label: "Error Text",
                    min: 1,
                    max: 1,
                },
            ],
        },
        {
            name: "participatedText",
            items: [
                {
                    type: "BasicText",
                    label: "Participated Text",
                    min: 1,
                    max: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantTop",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptions,
                        display: types.OptionsDisplay.Select,
                    },
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantBottom",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptions,
                        display: types.OptionsDisplay.Select,
                    },
                },
            ],
        },
    ],
}

export default Nps
